import React, { useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { Vehicle } from '@/services/api/vehicles';
import { VehicleStatus, getBusinessUnitLabel } from '@/types/common';
import { getCompanyEntityLabel } from '@/types/purchase-order';
import { FilePreviewCard } from '@components/FilePreviewCard';
import FieldDisplay from '../FieldDisplay';
import InfoSection from '../InfoSection';
import HeaderSection from '../HeaderSection';

// Status label mapper to convert uppercase enum values to user-friendly labels
const statusLabelMapper: Record<VehicleStatus, string> = {
  [VehicleStatus.IDLE]: 'Idle',
  [VehicleStatus.ACTIVE]: 'Active',
  [VehicleStatus.LOST]: 'Lost',
  [VehicleStatus.IN_REPAIR]: 'In Repair',
  [VehicleStatus.BROKEN]: 'Broken',
  [VehicleStatus.RETURNED]: 'Returned',
};

interface FormData {
  platNomor: string;
  vin: string;
  imei: string;
  oemID: string;
  modelID: string;
  color: string;
  status?: string;
  stnkAnnualExpiryDate?: string;
  stnkAnnualFileUrl?: string;
  stnkFiveYearExpiryDate?: string;
  stnkFiveYearFileUrl?: string;
  appUsername: string;
  appPassword: string;
  contractDuration?: number;
  contractStartDate?: string;
  contractEndDate?: string;
  arriveAt?: string | null;
  unitEquipment: string[];
  vehicleImageURL: string;
  notes: string;
}

interface HandoverDetailViewProps {
  detailTab: 'details' | 'equipment';
  formData: FormData;
  initialData?: Vehicle | null;
  vehicleEquipment: string[];
  onTabChange: (tab: 'details' | 'equipment') => void;
}

const HandoverDetailView: React.FC<HandoverDetailViewProps> = ({
  detailTab,
  formData,
  initialData,
  vehicleEquipment,
  onTabChange,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [detailTab]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 flex-shrink-0">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => onTabChange('details')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              detailTab === 'details'
                ? 'border-[#5E2AAC]'
                : 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-300'
            }`}
          >
            Vehicle Details
          </button>
          <button
            onClick={() => onTabChange('equipment')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              detailTab === 'equipment'
                ? 'border-[#5E2AAC]'
                : 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-300'
            }`}
          >
            Equipment Checklist
          </button>
        </nav>
      </div>

      {/* Detail View Content */}
      <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
        {detailTab === 'details' ? (
          <div className="space-y-6 mt-4">
            <HeaderSection title="Vehicle Detail" />
            <div className="grid grid-cols-2 gap-4 px-6">
              {/* Row 1: Hub Location & Business Unit with bottom border */}
              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Hub Location
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {(initialData as any)?.hubName || '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Business Unit
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {getBusinessUnitLabel((initialData as any)?.businessUnit)}
                  </p>
                </div>
              </div>
              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Entity
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {getCompanyEntityLabel((initialData as any)?.entity)}
                  </p>
                </div>
                <div className="flex-1 pl-4" />
              </div>
              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Plate Number
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {(initialData as any)?.platNomor || '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Color
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {(initialData as any)?.color || '-'}
                  </p>
                </div>
              </div>

              {/* Row 2: OEM Name & Model Name with bottom border */}
              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    OEM Name
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {(initialData as any)?.oemName || '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Model Name
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {(initialData as any)?.modelName || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="grid grid-cols-2 gap-4 px-6">
                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    Status
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {(() => {
                      const status =
                        formData?.status || (initialData as any)?.status;
                      if (!status) return '-';

                      // Use the enum mapper if status matches a VehicleStatus enum value
                      if (
                        Object.values(VehicleStatus).includes(
                          status as VehicleStatus
                        )
                      ) {
                        return statusLabelMapper[status as VehicleStatus];
                      }

                      // Fallback to original capitalization logic for other values
                      return (
                        status.charAt(0).toUpperCase() +
                        status.slice(1).toLowerCase()
                      );
                    })()}
                  </p>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    Nomor Rangka / VIN
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                    {formData?.vin || (initialData as any)?.vin || '-'}
                  </p>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    Nomor Mesin
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                    {formData?.imei || (initialData as any)?.imei || '-'}
                  </p>
                </div>

                {(initialData as any)?.stnkLink && (
                  <div className="col-span-2 border-b border-gray-200 flex-1">
                    <label className="block text-sm font-medium text-gray-400">
                      STNK Link
                    </label>
                    <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md break-all">
                      <a
                        href={(initialData as any).stnkLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#5E2AAC] hover:underline"
                      >
                        {(initialData as any).stnkLink}
                      </a>
                    </p>
                  </div>
                )}

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    App Username
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                    {formData.appUsername || (initialData as any)?.appUsername}
                  </p>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    App Password
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                    {formData.appPassword || (initialData as any)?.appPassword}
                  </p>
                </div>
              </div>
            </div>

            <HeaderSection title="Document Detail" />
            <div className="space-y-4 px-6">
              <div className="border-b border-gray-200 flex-1 pb-4">
                <label className="block text-sm font-medium text-gray-400">
                  STNK (PDF)
                </label>
                <div className="mt-1">
                  {formData?.stnkAnnualFileUrl ? (
                    <FilePreviewCard
                      fileName={formData.stnkAnnualFileUrl.split('/').pop() || '-'}
                    />
                  ) : (
                    <p className="text-sm font-semibold px-1 py-2 rounded-md">
                      -
                    </p>
                  )}
                </div>
              </div>

              <div className="border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Masa Berlaku Tahunan
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {formData?.stnkAnnualExpiryDate
                      ? dayjs(formData.stnkAnnualExpiryDate).format(
                          'DD/MM/YYYY'
                        )
                      : '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Masa Berlaku 5 Tahunan
                  </label>
                  <p className="mt-1 text-sm font-semibold px-1 py-2 rounded-md">
                    {formData?.stnkFiveYearExpiryDate
                      ? dayjs(formData.stnkFiveYearExpiryDate).format(
                          'DD/MM/YYYY'
                        )
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            <HeaderSection title="Contract Detail" />
            <div className="space-y-4 px-6">
              <div className="col-span-2 border-b border-gray-200 flex-1">
                <label className="block text-sm font-medium text-gray-400">
                  Contract Duration
                </label>
                <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                  {formData?.contractDuration
                    ? formData.contractDuration + ' Months'
                    : '-'}
                </p>
              </div>

              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Start Contract
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                    {formData.contractStartDate ||
                      (initialData as any)?.contractStartDate ||
                      '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4">
                  <label className="block text-sm font-medium text-gray-400">
                    End Contract
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                    {formData.contractEndDate ||
                      (initialData as any)?.contractEndDate ||
                      '-'}
                  </p>
                </div>
              </div>

              <div className="col-span-2 border-b border-gray-200 flex-1">
                <label className="block text-sm font-medium text-gray-400">
                  Tanggal & Waktu Kedatangan
                </label>
                <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md">
                  {formData.arriveAt || (initialData as any)?.arriveAt
                    ? dayjs(
                        formData.arriveAt || (initialData as any)?.arriveAt
                      ).format('DD/MM/YYYY, HH:mm')
                    : '-'}
                </p>
              </div>
            </div>

            {/* Info Section */}
            <div className="mt-6">
              <HeaderSection title="Info" />
              <div className="grid grid-cols-2 gap-4 px-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Created By
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.createdBy || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Created Date
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.createdAt
                      ? new Date(
                          (initialData as any).createdAt
                        ).toLocaleDateString('en-GB') +
                        ' at ' +
                        new Date(
                          (initialData as any).createdAt
                        ).toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Last Updated By
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.updatedBy || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Last Updated
                  </label>
                  <p className="mt-1 text-sm font-semibold  px-1 py-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.updatedAt
                      ? new Date(
                          (initialData as any).updatedAt
                        ).toLocaleDateString('en-GB') +
                        ' at ' +
                        new Date(
                          (initialData as any).updatedAt
                        ).toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="p-6 pb-0">
              <FieldDisplay
                label="Foto Kendaraan"
                value={
                  formData?.vehicleImageURL ||
                  (initialData as any)?.vehicleHandoverImageURL ||
                  (initialData as any)?.vehicleImageURL ? (
                    <div className="rounded-lg overflow-hidden mt-2 mb-2 border border-gray-100">
                      <img
                        src={
                          formData?.vehicleImageURL ||
                          (initialData as any)?.vehicleHandoverImageURL ||
                          (initialData as any)?.vehicleImageURL
                        }
                        alt="Vehicle"
                        className="w-full h-40 object-cover bg-gray-100"
                      />
                    </div>
                  ) : (
                    <div className="border-2 border-gray-200 rounded-lg mt-2 mb-2">
                      <div className="p-8 flex flex-col justify-center items-center text-center">
                        <div className="text-gray-300 text-4xl mb-4">
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            className="w-12 h-12 mx-auto"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <p className="text-gray-500 font-medium">
                          No image uploaded
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Image was not provided during handover
                        </p>
                      </div>
                    </div>
                  )
                }
                hasBorder={true}
              />

              <FieldDisplay
                className="mt-2"
                label="Kelengkapan Unit"
                value={
                  vehicleEquipment.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1 mt-1">
                      {vehicleEquipment.map((equipment) => (
                        <li key={equipment} className="text-sm text-gray-700">
                          {equipment}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">no equipment</p>
                  )
                }
                hasBorder={true}
              />

              <FieldDisplay
                className="mt-2"
                label="Notes"
                value={
                  <div className="whitespace-pre-wrap">
                    {formData.notes || (initialData as any)?.notes}
                  </div>
                }
                hasBorder={false}
              />
            </div>

            <div className="">
              <InfoSection data={initialData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HandoverDetailView;
